import identifiers from "@common/util/identifiers.json";
import {
  AccountLayout,
  AdministrationLayout,
  EmptyOutletLayout,
  InfoLayout,
  MainLayout,
  RootLayout,
} from "@main/layouts/Layouts";
import { AppLevelLayout } from "@main/layouts/views/AppLevelLayout";
import { ModuleLayoutView } from "@main/layouts/views/ModuleLayoutView";
import { WorkspaceRootLayout } from "@main/layouts/views/WorkspaceRootLayout";
import { AccountInfoPage, NotificationsConfirmationPage, NotificationsPage } from "@main/pages/Account/Account";
import { AddAppPage } from "@main/pages/AddApp/AddAppPage";
import {
  AdministrationAppDomainVerificationPage,
  AdministrationBillingPage,
  AdministrationCommonCommentsPage,
  AdministrationDomainVerificationPage,
  AdministrationExtensionsPage,
  AdministrationGeneralPage,
  AdministrationMembersPage,
} from "@main/pages/Administration/Administration";
import AppsCrashesView from "@main/pages/Administration/views/AppsView/AppsCrashesView";
import AppsFeedbackView from "@main/pages/Administration/views/AppsView/AppsFeedbackView";
import AppsGeneralView from "@main/pages/Administration/views/AppsView/AppsGeneralView";
import { AppsViewModule } from "@main/pages/Administration/views/AppsView/AppsModuleLayout";
import AppsRedirect from "@main/pages/Administration/views/AppsView/AppsRedirect";
import AppsUsersView from "@main/pages/Administration/views/AppsView/AppsUsersView";
import { AuthorizePage } from "@main/pages/Authorize/Authorize";
import { CannyAuthPage } from "@main/pages/Canny/CannyAuth";
import { ChangeEmailConfirmationPage } from "@main/pages/ChangeEmailConfirmation/ChangeEmailConfirmation";
import ChromeExtensionWelcomePage from "@main/pages/ChromeExtension/ChromeExtensionWelcomePage";
import { ConfirmSignUpPage } from "@main/pages/ConfirmSignUp/ConfirmSignUp";
import {
  CrashesPage,
  CrashEventActivityHistoryPage,
  CrashEventPage,
  CrashOverviewPage,
} from "@main/pages/Crashes/Crashes";
import { CrashesModulePage } from "@main/pages/Crashes/views/CrashesModulePage";
import { ForgotPasswordPage } from "@main/pages/ForgotPassword/ForgotPassword";
import HowToAll from "@main/pages/Info/views/HowTo/HowToAll";
import HowToCustomerSupport from "@main/pages/Info/views/HowTo/HowToCustomerSupport";
import HowToDevelopers from "@main/pages/Info/views/HowTo/HowToDevelopers";
import HowToManagers from "@main/pages/Info/views/HowTo/HowToManagers";
import HowToTesters from "@main/pages/Info/views/HowTo/HowToTesters";
import { InfoHowToModule } from "@main/pages/Info/views/HowTo/InfoHowToModule";
import UsefulLinks from "@main/pages/Info/views/UsefulLinks/UsefulLinks";
import { IntegrationActivation } from "@main/pages/IntegrationActivation/IntegrationActivation";
import NoSubscriptionPage from "@main/pages/NoSubscription/NoSubscription";
import NotFoundPage from "@main/pages/NotFound/NotFoundPage";
import { ReleaseMetricDetailsPage, ReleasesPage } from "@main/pages/Release/Release";
import MetricAboutView from "@main/pages/Release/views/MetricDetails/MetricAboutView";
import MetricDataView from "@main/pages/Release/views/MetricDetails/MetricDataView";
import MetricGuardrailsView from "@main/pages/Release/views/MetricDetails/MetricGuardrailsView";
import MetricMetadataDimensions from "@main/pages/Release/views/MetricDetails/MetricMetadataDimensions";
import MetricScoreView from "@main/pages/Release/views/MetricDetails/MetricScoreView";
import { ReleasesModulePage } from "@main/pages/Release/views/ReleasesModulePage";
import { ResetPasswordPage } from "@main/pages/ResetPassword/ResetPassword";
import { ShareShakePage } from "@main/pages/ShareShake/ShareShakePage";
import { SignInPage } from "@main/pages/SignIn/SignIn";
import { SignUpPage } from "@main/pages/SignUp/SignUp";
import {
  UserFeedbackActivityHistoryPage,
  UserFeedbackDetailsPage,
  UserFeedbackListPage,
} from "@main/pages/UserFeedback/UserFeedback";
import { UserFeedbackModulePage } from "@main/pages/UserFeedback/views/UserFeedbackModulePage";
import { UsersDetailPage, UsersPage } from "@main/pages/Users/Users";
import { UsersModulePage } from "@main/pages/Users/views/UsersModulePage";
import { WorkspaceCreatePage } from "@main/pages/WorkspaceCreate/WorkspaceCreate";
import { WorkspaceJoinPage } from "@main/pages/WorkspaceJoin/WorkspaceJoin";
import { WorkspaceJoinInvitationPage } from "@main/pages/WorkspaceJoinInvitation/WorkspaceJoinInvitation";
import AccountRedirect from "@main/router/redirect/AccountRedirect";
import { AdministrationRedirect } from "@main/router/redirect/AdministrationRedirect";
import { CrashesRedirect } from "@main/router/redirect/CrashesRedirect";
import { HowToRedirect } from "@main/router/redirect/Info/HowToRedirect";
import { InfoRedirect } from "@main/router/redirect/Info/InfoRedirect";
import { ReleaseMetricRedirect } from "@main/router/redirect/ReleaseMetricRedirect";

import { RoutePaths } from "./routePaths";

export enum RouteConfigType {
  PATH,
  INDEX,
  LAYOUT,
}

export interface PathRouteConfig {
  name: string;
  type: RouteConfigType.PATH;
  path: RoutePaths;
  element?: () => JSX.Element;
  subRoutes?: RouteConfig[];
  testId?: string;
  protected?: boolean;
}

export interface IndexRouteConfig {
  type: RouteConfigType.INDEX;
  index: true;
  element?: () => JSX.Element;
}

export interface LayoutRouteConfig {
  type: RouteConfigType.LAYOUT;
  element: () => JSX.Element;
  subRoutes: RouteConfig[];
}

export type RouteConfig = PathRouteConfig | IndexRouteConfig | LayoutRouteConfig;

export const userFeedbackRouteConfigs: RouteConfig[] = [
  {
    name: "Feedback",
    type: RouteConfigType.PATH,
    path: RoutePaths.USER_FEEDBACK,
    element: UserFeedbackListPage,
    testId: identifiers["user.feedback.header"],
  },
  {
    name: "Feedback",
    type: RouteConfigType.PATH,
    path: RoutePaths.USER_FEEDBACK_DETAILS,
    element: UserFeedbackDetailsPage,
    testId: identifiers["user.feedback.header"],
  },
  {
    name: "Feedback",
    type: RouteConfigType.PATH,
    path: RoutePaths.USER_FEEDBACK_LOGS,
    element: UserFeedbackActivityHistoryPage,
  },
  {
    name: "Feedback",
    type: RouteConfigType.PATH,
    path: RoutePaths.USER_FEEDBACK_SHARE_SHAKE,
    element: ShareShakePage,
  },
];

export const crashReportingRouteConfigs: RouteConfig[] = [
  {
    name: "Crashes",
    type: RouteConfigType.PATH,
    path: RoutePaths.CRASH_REPORTS,
    element: CrashesPage,
    testId: identifiers["crash.reports.header"],
  },
  {
    name: "Crashes",
    type: RouteConfigType.PATH,
    path: RoutePaths.CRASH_REPORT_SHARE_SHAKE,
    element: ShareShakePage,
  },
  {
    name: "Crashes",
    type: RouteConfigType.PATH,
    path: RoutePaths.CRASH_REPORT_ROOT,
    element: EmptyOutletLayout,
    testId: identifiers["crash.reports.header"],
    subRoutes: [
      {
        name: "Crashes",
        type: RouteConfigType.PATH,
        path: RoutePaths.CRASH_REPORT_OVERVIEW,
        element: CrashOverviewPage,
        testId: identifiers["crash.reports.header"],
      },
      {
        name: "Crashes",
        type: RouteConfigType.PATH,
        path: RoutePaths.CRASH_REPORT_EVENTS,
        element: EmptyOutletLayout,
        testId: identifiers["crash.reports.header"],
        subRoutes: [
          {
            name: "Crashes",
            type: RouteConfigType.PATH,
            path: RoutePaths.CRASH_REPORT_EVENT,
            element: CrashEventPage,
            testId: identifiers["crash.reports.header"],
          },
          {
            name: "Crashes",
            type: RouteConfigType.PATH,
            path: RoutePaths.CRASH_REPORT_LOGS,
            element: CrashEventActivityHistoryPage,
            testId: identifiers["crash.reports.header"],
          },
          {
            type: RouteConfigType.INDEX,
            index: true,
            element: CrashesRedirect,
          },
        ],
      },
      {
        type: RouteConfigType.INDEX,
        index: true,
        element: CrashesRedirect,
      },
    ],
  },
];

export const usersRouteConfigs: RouteConfig[] = [
  {
    name: "Users",
    type: RouteConfigType.PATH,
    path: RoutePaths.USERS,
    element: UsersPage,
    testId: identifiers["users.header"],
  },
  {
    name: "Users",
    type: RouteConfigType.PATH,
    path: RoutePaths.USER_DETAILS,
    element: UsersDetailPage,
    testId: identifiers["users.header"],
  },
  {
    name: "Users",
    type: RouteConfigType.PATH,
    path: RoutePaths.USERS_SHARE_SHAKE,
    element: ShareShakePage,
  },
];

export const releaseMetricRouteConfigs: RouteConfig[] = [
  {
    name: "Data",
    type: RouteConfigType.PATH,
    path: RoutePaths.RELEASE_METRIC_DATA,
    element: MetricDataView,
  },
  {
    name: "Score",
    type: RouteConfigType.PATH,
    path: RoutePaths.RELEASE_METRIC_SCORE,
    element: MetricScoreView,
  },
  {
    name: "Guardrails",
    type: RouteConfigType.PATH,
    path: RoutePaths.RELEASE_METRIC_GUARDRAILS,
    element: MetricGuardrailsView,
  },
  {
    name: "About",
    type: RouteConfigType.PATH,
    path: RoutePaths.RELEASE_METRIC_ABOUT,
    element: MetricAboutView,
  },
  {
    name: "Metadata dimensions",
    type: RouteConfigType.PATH,
    path: RoutePaths.RELEASE_METRIC_METADATA_DIMENSIONS,
    element: MetricMetadataDimensions,
  },
  {
    type: RouteConfigType.INDEX,
    index: true,
    element: ReleaseMetricRedirect,
  },
];

export const releasesRouteConfigs: RouteConfig[] = [
  {
    name: "Releases",
    type: RouteConfigType.PATH,
    path: RoutePaths.RELEASES,
    element: ReleasesPage,
  },
  {
    name: "Release metric root",
    type: RouteConfigType.PATH,
    path: RoutePaths.RELEASE_METRIC_ROOT,
    element: ReleaseMetricDetailsPage,
    subRoutes: releaseMetricRouteConfigs,
  },
];

export const accountRouteConfigs: RouteConfig[] = [
  {
    name: "Personal info",
    type: RouteConfigType.PATH,
    path: RoutePaths.ACCOUNT_PERSONAL_INFO,
    element: AccountInfoPage,
    testId: identifiers["myAccount.button.info"],
  },
  {
    name: "Notifications",
    type: RouteConfigType.PATH,
    path: RoutePaths.ACCOUNT_NOTIFICATIONS,
    element: NotificationsPage,
    testId: identifiers["myAccount.button.notifications"],
  },
  {
    name: "Notifications confirmation",
    type: RouteConfigType.PATH,
    path: RoutePaths.ACCOUNT_NOTIFICATIONS_CONFIRMATION,
    element: NotificationsConfirmationPage,
  },
  {
    type: RouteConfigType.INDEX,
    index: true,
    element: AccountRedirect,
  },
];

export const appsRouteConfigsWithKey: RouteConfig[] = [
  {
    name: "General",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_APPS_GENERAL_KEY,
    element: AppsGeneralView,
  },
  {
    name: "Feedback",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_APPS_USER_FEEDBACK_KEY,
    element: AppsFeedbackView,
  },
  {
    name: "Crashes",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_APPS_CRASHES_KEY,
    element: AppsCrashesView,
  },
  {
    name: "Users and chat",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_APPS_USERS_KEY,
    element: AppsUsersView,
  },
];

export const appsRouteConfigs: RouteConfig[] = [
  {
    name: "General",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_APPS_GENERAL,
    element: AppsGeneralView,
  },
  {
    name: "Feedback",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_APPS_USER_FEEDBACK,
    element: AppsFeedbackView,
  },
  {
    name: "Crashes",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_APPS_CRASHES,
    element: AppsCrashesView,
  },
  {
    name: "Users and chat",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_APPS_USERS,
    element: AppsUsersView,
  },
];

export const joinedAppRouteConfigs: RouteConfig[] = appsRouteConfigsWithKey.concat(appsRouteConfigs).concat([
  {
    type: RouteConfigType.INDEX,
    index: true,
    element: AppsRedirect,
  },
]);

export const administrationRouteConfigs: RouteConfig[] = [
  {
    name: "General",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_GENERAL,
    element: AdministrationGeneralPage,
    testId: identifiers["workspaceAdministration.button.general"],
  },
  {
    name: "Extensions",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_EXTENSIONS,
    element: AdministrationExtensionsPage,
    testId: identifiers["workspaceAdministration.button.extensions"],
  },
  {
    name: "Members",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_MEMBERS,
    element: AdministrationMembersPage,
    testId: identifiers["workspaceAdministration.button.members"],
  },
  {
    name: "Apps",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_APPS,
    element: AppsViewModule,
    testId: identifiers["workspaceAdministration.button.apps"],
    subRoutes: joinedAppRouteConfigs,
  },
  {
    name: "Common comments",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_COMMON_COMMENTS,
    element: AdministrationCommonCommentsPage,
  },

  {
    name: "Billing",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_BILLING,
    element: AdministrationBillingPage,
    testId: identifiers["workspace.administration.button.billing"],
  },
  {
    type: RouteConfigType.INDEX,
    index: true,
    element: AdministrationRedirect,
  },
];

export const infoHowToRouteConfigs: RouteConfig[] = [
  {
    name: "Product managers",
    type: RouteConfigType.PATH,
    path: RoutePaths.INFO_PRODUCT_MANAGERS,
    element: HowToManagers,
  },
  {
    name: "App developers",
    type: RouteConfigType.PATH,
    path: RoutePaths.INFO_APP_DEVELOPERS,
    element: HowToDevelopers,
  },
  {
    name: "App testers",
    type: RouteConfigType.PATH,
    path: RoutePaths.INFO_APP_TESTERS,
    element: HowToTesters,
  },
  {
    name: "Customer support",
    type: RouteConfigType.PATH,
    path: RoutePaths.INFO_CUSTOMER_SUPORT,
    element: HowToCustomerSupport,
  },
  {
    name: "All",
    type: RouteConfigType.PATH,
    path: RoutePaths.INFO_ALL,
    element: HowToAll,
  },
  {
    type: RouteConfigType.INDEX,
    index: true,
    element: HowToRedirect,
  },
];

export const infoRouteConfigs: RouteConfig[] = [
  {
    name: "Videos",
    type: RouteConfigType.PATH,
    path: RoutePaths.VIDEOS_ROOT,
    element: InfoHowToModule,
    subRoutes: infoHowToRouteConfigs,
  },
  {
    name: "Quick links",
    type: RouteConfigType.PATH,
    path: RoutePaths.QUICK_LINKS,
    element: UsefulLinks,
  },
  // {
  //   name: "App development",
  //   type: RouteConfigType.PATH,
  //   path: RoutePaths.APP_DEVELOPMENT,
  //   element: AppDevelopmentModule,
  // },
  {
    type: RouteConfigType.INDEX,
    index: true,
    element: InfoRedirect,
  },
];

export const workspaceRouteConfigs: RouteConfig[] = [
  {
    type: RouteConfigType.LAYOUT,
    element: ModuleLayoutView,
    subRoutes: [
      {
        name: "Feedback",
        type: RouteConfigType.PATH,
        path: RoutePaths.USER_FEEDBACK_MODULE_ROOT,
        element: UserFeedbackModulePage,
        subRoutes: userFeedbackRouteConfigs,
      },
      {
        name: "Crashes",
        type: RouteConfigType.PATH,
        path: RoutePaths.CRASH_MODULE_ROOT,
        element: CrashesModulePage,
        subRoutes: crashReportingRouteConfigs,
      },
      {
        name: "Users",
        type: RouteConfigType.PATH,
        path: RoutePaths.USERS_MODULE_ROOT,
        element: UsersModulePage,
        subRoutes: usersRouteConfigs,
      },
      {
        name: "Releases",
        type: RouteConfigType.PATH,
        path: RoutePaths.RELEASES_MODULE_ROOT,
        element: ReleasesModulePage,
        subRoutes: releasesRouteConfigs,
      },
    ],
  },
  {
    name: "Administration root",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_ROOT,
    element: AdministrationLayout,
    subRoutes: administrationRouteConfigs,
  },
  {
    name: "Info root",
    type: RouteConfigType.PATH,
    path: RoutePaths.INFO_ROOT,
    element: InfoLayout,
    subRoutes: infoRouteConfigs,
  },
  {
    name: "Account root",
    type: RouteConfigType.PATH,
    path: RoutePaths.ACCOUNT_ROOT,
    element: AccountLayout,
    subRoutes: accountRouteConfigs,
  },
  {
    name: "No subscription",
    type: RouteConfigType.PATH,
    path: RoutePaths.NO_SUBSCRIPTION,
    element: NoSubscriptionPage,
  },
  {
    name: "Add App",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADD_APP,
    element: AddAppPage,
  },
  {
    type: RouteConfigType.INDEX,
    index: true,
    element: InfoRedirect,
  },
];

export const internalRoutesConfig: RouteConfig[] = [
  {
    type: RouteConfigType.LAYOUT,
    element: RootLayout,
    subRoutes: [
      {
        name: "Authorize",
        type: RouteConfigType.PATH,
        path: RoutePaths.AUTHORIZE,
        element: AuthorizePage,
      },
      {
        name: "Main",
        type: RouteConfigType.PATH,
        path: RoutePaths.ROOT,
        element: MainLayout,
        subRoutes: [
          {
            name: "Workspace root",
            type: RouteConfigType.PATH,
            path: RoutePaths.WORKSPACE_ROOT,
            element: WorkspaceRootLayout,
            subRoutes: workspaceRouteConfigs,
          },
        ],
      },
      {
        name: "Join workspace",
        type: RouteConfigType.PATH,
        path: RoutePaths.JOIN_WORKSPACE,
        element: WorkspaceJoinPage,
      },
      {
        name: "Create workspace",
        type: RouteConfigType.PATH,
        path: RoutePaths.CREATE_WORKSPACE,
        element: WorkspaceCreatePage,
      },

      {
        name: "Canny Auth",
        type: RouteConfigType.PATH,
        path: RoutePaths.CANNY_AUTH,
        element: CannyAuthPage,
      },
      {
        name: "Integration activation",
        type: RouteConfigType.PATH,
        path: RoutePaths.INTEGRATION_ACTIVATION,
        element: IntegrationActivation,
      },
      {
        name: "Change email confirmation",
        type: RouteConfigType.PATH,
        path: RoutePaths.CHANGE_EMAIL_CONFIRMATION,
        element: ChangeEmailConfirmationPage,
      },
    ],
  },
  {
    name: "Sign in",
    type: RouteConfigType.PATH,
    path: RoutePaths.SIGNIN,
    element: SignInPage,
  },
  {
    name: "Sign out",
    type: RouteConfigType.PATH,
    path: RoutePaths.SIGNUP,
    element: SignUpPage,
  },
  {
    name: "Confirm sign up",
    type: RouteConfigType.PATH,
    path: RoutePaths.CONFIRM_SIGNUP,
    element: ConfirmSignUpPage,
  },
  {
    name: "Forgot password",
    type: RouteConfigType.PATH,
    path: RoutePaths.FORGOT_PASSWORD,
    element: ForgotPasswordPage,
  },
  {
    name: "Reset password",
    type: RouteConfigType.PATH,
    path: RoutePaths.RESET_PASSWORD,
    element: ResetPasswordPage,
  },
  {
    name: "Accept Invitation",
    type: RouteConfigType.PATH,
    path: RoutePaths.WORKSPACE_JOIN_INVITATION,
    element: WorkspaceJoinInvitationPage,
  },
  {
    name: "Approved domain email verification",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_DOMAIN_VERIFICATION,
    element: AdministrationDomainVerificationPage,
  },
  {
    name: "App domain email verification",
    type: RouteConfigType.PATH,
    path: RoutePaths.ADMINISTRATION_APP_DOMAIN_VERIFICATION,
    element: AdministrationAppDomainVerificationPage,
  },
  {
    name: "Not found",
    type: RouteConfigType.PATH,
    path: RoutePaths.NOT_FOUND,
    element: NotFoundPage,
  },
  {
    name: "Shake extension welcome",
    type: RouteConfigType.PATH,
    path: RoutePaths.CHROME_EXTENSION_WELCOME,
    element: ChromeExtensionWelcomePage,
  },
];

export const routesConfig: RouteConfig[] = [
  { type: RouteConfigType.LAYOUT, element: AppLevelLayout, subRoutes: internalRoutesConfig },
];
